// The set-out drawing as inline SVG: rooms from the measure, doors as plain
// gaps, plank direction arrows in every floored room, the start row in ember.
// Labels inside the SVG are the one place type may scale with the drawing.
import { useT } from "../i18n";
import type { SetOutPlan } from "../lib/types";

export function SetOutDrawing({ plan, className }: { plan: SetOutPlan; className?: string }) {
  const { t } = useT();
  const W = 360, H = 250;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={className} role="img" aria-label={t("setout_drawing")} style={{ width: "100%", height: "auto", display: "block" }}>
      <rect x="0" y="0" width={W} height={H} fill="hsl(var(--background))" />
      {Array.from({ length: W / 20 + 1 }, (_, i) => <line key={`gx${i}`} x1={i * 20} y1="0" x2={i * 20} y2={H} stroke="hsl(var(--border))" strokeWidth="0.5" />)}
      {Array.from({ length: H / 20 + 1 }, (_, i) => <line key={`gy${i}`} x1="0" y1={i * 20} x2={W} y2={i * 20} stroke="hsl(var(--border))" strokeWidth="0.5" />)}
      {plan.rooms.map((r) => (
        <rect key={`r${r.name}`} x={r.x} y={r.y} width={r.w} height={r.h} fill={r.hl ? "hsl(var(--accent))" : "hsl(var(--card))"} stroke="hsl(var(--foreground))" strokeWidth="1.5" />
      ))}
      {plan.rooms.map((r) => (
        <g key={`l${r.name}`} fontFamily="'IBM Plex Sans', system-ui, sans-serif">
          <text x={r.x + 8} y={r.y + 16} fontSize="11" fontWeight="600" fill="hsl(var(--foreground))">{r.name}</text>
          {r.dim && <text x={r.x + 8} y={r.y + 28} fontSize="9.5" fill="hsl(var(--muted-foreground))">{r.dim}</text>}
          {r.area && <text x={r.x + 8} y={r.y + 40} fontSize="9.5" fontFamily="'IBM Plex Mono', monospace" fill="hsl(var(--muted-foreground))">{r.area}</text>}
          {r.area && r.w > 60 && r.h > 50 && (
            plan.direction === "across"
              ? <path d={`M${r.x + 14} ${r.y + r.h - 16}H${r.x + r.w - 14}M${r.x + r.w - 20} ${r.y + r.h - 20}l6 4-6 4`} fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" />
              : <path d={`M${r.x + r.w - 16} ${r.y + 48}V${r.y + r.h - 14}M${r.x + r.w - 20} ${r.y + r.h - 20}l4 6 4-6`} fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" />
          )}
        </g>
      ))}
      {plan.doors.map((d, i) => <line key={`d${i}`} x1={d[0]} y1={d[1]} x2={d[2]} y2={d[3]} stroke="hsl(var(--card))" strokeWidth="4" />)}
      {plan.start && (
        <g>
          <line x1={plan.start[0]} y1={plan.start[1]} x2={plan.start[2]} y2={plan.start[3]} stroke="hsl(var(--warning))" strokeWidth="2" strokeDasharray="4 3" />
          <text x={plan.start[0] + 4} y={plan.start[1] - 5} fontSize="9.5" fontFamily="'IBM Plex Sans', system-ui, sans-serif" fill="hsl(var(--warning))">{t("start_row")}</text>
        </g>
      )}
    </svg>
  );
}
